import { useCallback } from 'react';

import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import type { XYChartOnEventPayload } from '../../../xy-chart/types/xy-chart-props.js';
import { FLAME_ORIGINAL_IDX } from '../constants.js';
import { collectSubtree } from '../utils/collect-subtree.js';

type FlameOnClickCallback<TData> = (
  payload:
    | { datapoint: TData; descendants: TData[] }
    | { datapoint: undefined; descendants: [] },
) => void;

type UseFlameOnEventOptions<TData extends ChartDatapoint> = {
  onClick: FlameOnClickCallback<TData> | undefined;
  data: TData[];
  idAccessor: string;
  childrenByParentId: Map<unknown, TData[]>;
};

/**
 * @internal
 */
export const useFlameOnEvent = <TData extends ChartDatapoint>({
  onClick,
  data,
  idAccessor,
  childrenByParentId,
}: UseFlameOnEventOptions<TData>):
  | ((payload: XYChartOnEventPayload) => void)
  | undefined => {
  const handler = useCallback(
    (payload: XYChartOnEventPayload) => {
      if (!onClick) {
        return;
      }
      if (payload.datapoint === undefined) {
        onClick({ datapoint: undefined, descendants: [] });
        return;
      }
      const originalIdx = payload.datapoint[FLAME_ORIGINAL_IDX] as
        | number
        | undefined;
      if (originalIdx === undefined) {
        return;
      }
      const original = data[originalIdx] as TData;
      const clickedId = original[idAccessor];
      const subtree = collectSubtree(
        payload.datapoint as TData,
        clickedId,
        childrenByParentId,
        idAccessor,
      );
      const descendants = subtree
        .slice(1)
        .map((d) => data[d[FLAME_ORIGINAL_IDX] as number] as TData);
      onClick({ datapoint: original, descendants });
    },
    [onClick, data, idAccessor, childrenByParentId],
  );

  return onClick !== undefined ? handler : undefined;
};
